import { motion } from 'framer-motion';
import { useState } from 'react';

import { Button } from '@aurum/ui';

import { api, type SommelierRecommendation } from '../../lib/api';
import { fixtureSommelier } from '../../lib/fixtures';

const USE_FIXTURES: boolean = String(import.meta.env.VITE_USE_FIXTURES ?? 'false') === 'true';

const QUICK_MOODS: ReadonlyArray<{ key: string; label: string }> = [
  { key: 'morning ritual',         label: 'صباح هادئ' },
  { key: 'afternoon energy',       label: 'ظهر نشِط' },
  { key: 'cold winter evening',    label: 'مساء شتوي' },
  { key: 'celebration',            label: 'مناسبة' },
  { key: 'something refreshing',   label: 'منعش' },
  { key: 'arabian exotic flavors', label: 'نكهة عربية' },
];

export function SommelierBar(): JSX.Element {
  const [mood, setMood] = useState('');
  const [picks, setPicks] = useState<SommelierRecommendation[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function ask(text: string): Promise<void> {
    const trimmed = text.trim();
    if (trimmed.length < 2) return;
    setLoading(true);
    setError(null);
    try {
      const result = USE_FIXTURES ? fixtureSommelier(trimmed) : await api.sommelier(trimmed);
      // Tiny delay in fixtures mode for nicer UX (avoid jarring instant render).
      if (USE_FIXTURES) await new Promise((r) => setTimeout(r, 220));
      setPicks(result);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Sommelier failed');
    } finally {
      setLoading(false);
    }
  }

  return (
    <section className="mb-12 rounded-aurum-lg border border-aurum-500/20 bg-onyx-700/40 p-6">
      <header className="mb-3">
        <h2 className="font-display text-2xl text-aurum-200">الـ Sommelier الرقمي</h2>
        <p className="mt-1 font-body text-xs text-cream-100/60">
          اصف مزاجك أو لحظتك — سأقترح لك ما يناسبك.
        </p>
      </header>

      <form
        onSubmit={(e) => {
          e.preventDefault();
          void ask(mood);
        }}
        className="flex flex-col gap-3 md:flex-row"
      >
        <input
          value={mood}
          onChange={(e) => setMood(e.target.value)}
          placeholder="مثلاً: صباح هادئ بعد ليلة طويلة"
          className="flex-1 rounded-aurum-md border border-aurum-500/30 bg-onyx-900/60 px-4 py-3 font-body text-sm text-cream-50 placeholder:text-onyx-100/60 focus:border-aurum-400 focus:outline-none"
        />
        <Button type="submit" isLoading={loading}>
          اقترح
        </Button>
      </form>

      <div className="mt-3 flex flex-wrap gap-2">
        {QUICK_MOODS.map((m) => (
          <button
            key={m.key}
            type="button"
            onClick={() => {
              setMood(m.key);
              void ask(m.key);
            }}
            className="rounded-full border border-aurum-500/20 px-3 py-1 text-xs text-cream-100/70 hover:border-aurum-500/40 hover:text-aurum-200"
          >
            {m.label}
          </button>
        ))}
      </div>

      {error ? (
        <p className="mt-3 text-sm text-accent-ruby">{error}</p>
      ) : null}

      {picks.length > 0 ? (
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          className="mt-5 grid grid-cols-1 gap-3 md:grid-cols-2"
        >
          {picks.map((p) => (
            <article
              key={p.itemId}
              className="rounded-aurum-md border border-aurum-500/20 bg-onyx-900/40 p-4"
            >
              <div className="flex items-baseline justify-between">
                <p className="font-display text-lg text-aurum-200">{p.nameAr}</p>
                <span className="font-mono text-xs text-aurum-300/80">
                  {(p.score * 100).toFixed(0)}% ملاءمة
                </span>
              </div>
              <p className="mt-1 font-body text-xs uppercase tracking-widest text-aurum-400">
                {p.nameEn}
              </p>
              {p.reason ? (
                <p className="mt-2 font-body text-xs text-cream-100/70">{p.reason}</p>
              ) : null}
            </article>
          ))}
        </motion.div>
      ) : null}
    </section>
  );
}
